import {
  CheckCircleFilled,
  CloseCircleOutlined,
  CrownOutlined,
  LockOutlined,
  MinusCircleFilled,
  PlusOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Alert, Button, Drawer, Input, Select, Skeleton, Table, Tooltip, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  workspaceGetMemberAuthMatrix,
  workspacePutMemberAuthGrant,
  workspacePutTeamAuthSwitch,
  workspaceResetMemberAuthGrants,
} from '../../api'
import MemberAuthMatrixPanel from '../../platform/authMatrix/MemberAuthMatrixPanel'
import {
  getPermissionsOverview,
  listTeamMembers,
  putRoleCapabilities,
  setMemberRole,
  teamErrorCode,
  teamErrorMessage,
} from './teamApi'
import { effectiveLevel, grantedCount, groupCapabilities, isMatrixCellEditable, nextPermissionLevel } from './teamAudit'
import { canManageMemberAuth, canSelectMemberAuthTarget, canSetRole } from './teamPermissions'
import type {
  MemberStatus,
  PermissionCapability,
  PermissionLevel,
  PermissionsOverview,
  TeamMember,
  TeamRoleKey,
} from './teamTypes'
import { TeamAvatar } from './teamUi'

const PAGE_SIZE = 10
type View = 'members' | 'matrix' | 'function'

const MEMBER_AUTH_ACTOR = {
  getMatrix: workspaceGetMemberAuthMatrix,
  putGrant: workspacePutMemberAuthGrant,
  resetInherit: workspaceResetMemberAuthGrants,
  putTeamSwitch: workspacePutTeamAuthSwitch,
}

/** Matrix cell: level badge, entry link, or Owner-editable toggle. */
function LevelCell({
  row,
  role,
  noteText,
  canEditMatrix,
  savingKey,
  onToggle,
  onOpenEntry,
}: {
  row: PermissionCapability
  role: TeamRoleKey
  noteText?: string
  canEditMatrix?: boolean
  savingKey?: string | null
  onToggle?: (row: PermissionCapability, role: TeamRoleKey, next: PermissionLevel) => void
  onOpenEntry?: () => void
}) {
  const { t } = useTranslation('team')
  const level = effectiveLevel(row, role)
  if (level === 'entry') {
    return (
      <Button type="link" size="small" onClick={onOpenEntry} data-testid={`team-perm-entry-${row.key}`}>
        {t('perm.matrix.status.entry')}
      </Button>
    )
  }
  if (level === 'soon') return <span className="tm-perm-soon">{t('perm.matrix.status.coming_soon')}</span>
  if (level === 'unavailable') return <span className="tm-perm-soon">{t('perm.matrix.status.not_available')}</span>

  const editable = isMatrixCellEditable(row, role, !!canEditMatrix)
  const cellKey = `${row.key}:${role}`
  const busy = savingKey === cellKey
  const allowed = (row.allowed_levels?.[role] ?? []) as PermissionLevel[]
  const locked = row.locked_reasons?.[role]

  const icon =
    level === 'full' ? (
      <span className="tm-perm-level is-full" title={t('perm.matrix.level.full')}>
        <CheckCircleFilled aria-label={t('perm.matrix.level.full')} />
      </span>
    ) : level === 'limited' ? (
      <Tooltip title={noteText}>
        <span className="tm-perm-level is-limited" data-testid="team-perm-limited">
          <MinusCircleFilled aria-label={t('perm.matrix.level.limited')} />
          <span className="tm-perm-limited-text">{t('perm.matrix.level.limited')}</span>
        </span>
      </Tooltip>
    ) : (
      <span className="tm-perm-level is-none" title={t('perm.matrix.level.none')}>
        <CloseCircleOutlined aria-label={t('perm.matrix.level.none')} />
      </span>
    )

  if (!editable) {
    const tip = locked ? t(`perm.matrix.locked.${locked}`, { defaultValue: noteText || '' }) : noteText
    return tip ? <Tooltip title={tip}>{icon}</Tooltip> : icon
  }

  return (
    <Tooltip title={t('perm.matrix.clickToToggle')}>
      <button
        type="button"
        className="tm-perm-toggle"
        disabled={busy}
        data-testid={`team-perm-toggle-${row.key}-${role}`}
        aria-busy={busy}
        onClick={() => {
          if (!onToggle || busy) return
          onToggle(row, role, nextPermissionLevel(level, allowed))
        }}
      >
        {icon}
      </button>
    </Tooltip>
  )
}

/**
 * VistoeraTASK-00532 — 权限管理. Canonical role-based access control only:
 * the role cards, the member roster and the matrix all come from the backend,
 * which re-checks every write from the live membership. Future node / Provider
 * level permissions are shown as informational "即将支持" text — never as a
 * switch that looks like it does something.
 */
export default function TeamPermissionsTab({ workspaceId }: { workspaceId: string }) {
  const { t } = useTranslation('team')
  const navigate = useNavigate()
  const [view, setView] = useState<View>('members')
  const [overview, setOverview] = useState<PermissionsOverview | null>(null)
  const [overviewError, setOverviewError] = useState<string | null>(null)

  const [items, setItems] = useState<TeamMember[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [role, setRole] = useState<TeamRoleKey | undefined>()
  const [status, setStatus] = useState<MemberStatus | undefined>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<TeamMember | null>(null)
  const [authRoster, setAuthRoster] = useState<TeamMember[]>([])
  const [savingCell, setSavingCell] = useState<string | null>(null)
  const reqSeq = useRef(0)
  const overviewRef = useRef(overview)
  overviewRef.current = overview

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedQ(q.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(id)
  }, [q])

  const loadOverview = useCallback(async () => {
    setOverviewError(null)
    try {
      const res = await getPermissionsOverview(workspaceId)
      setOverview(res.data)
    } catch (e) {
      setOverviewError(teamErrorMessage(e, t('perm.loadFailed')))
    }
  }, [workspaceId, t])

  const loadMembers = useCallback(async () => {
    const seq = ++reqSeq.current
    setLoading(true)
    setError(null)
    try {
      const res = await listTeamMembers(workspaceId, { q: debouncedQ || undefined, role, status, page, page_size: PAGE_SIZE })
      if (seq !== reqSeq.current) return
      setItems(res.data.items)
      setTotal(res.data.total)
    } catch (e) {
      if (seq !== reqSeq.current) return
      setError(teamErrorMessage(e, t('perm.members.loadFailed')))
    } finally {
      if (seq === reqSeq.current) setLoading(false)
    }
  }, [workspaceId, debouncedQ, role, status, page, t])

  useEffect(() => {
    void loadOverview()
  }, [loadOverview])
  useEffect(() => {
    void loadMembers()
  }, [loadMembers])

  // Compact roster for the function-auth carousel (active members only, no filters).
  useEffect(() => {
    if (view !== 'function') return
    let cancelled = false
    void listTeamMembers(workspaceId, { status: 'active', page: 1, page_size: 100 })
      .then((res) => {
        if (!cancelled) setAuthRoster(res.data.items)
      })
      .catch(() => {
        if (!cancelled) setAuthRoster([])
      })
    return () => {
      cancelled = true
    }
  }, [view, workspaceId])

  const refreshAll = () => {
    void loadOverview()
    void loadMembers()
  }

  const canEditMatrix = !!overview?.you.can_edit_matrix

  const toggleCapability = useCallback(
    async (row: PermissionCapability, role: TeamRoleKey, next: PermissionLevel) => {
      if (role === 'owner' || !canEditMatrix) return
      const cellKey = `${row.key}:${role}`
      const prev = overviewRef.current
      if (!prev) return
      // Optimistic local update — roll back on failure.
      setOverview({
        ...prev,
        capabilities: prev.capabilities.map((c) =>
          c.key === row.key ? { ...c, levels: { ...c.levels, [role]: next } } : c,
        ),
      })
      setSavingCell(cellKey)
      try {
        const res = await putRoleCapabilities(workspaceId, [
          { capability_key: row.key, role_key: role as 'admin' | 'member', level: next },
        ])
        setOverview(res.data)
        void message.success(t('perm.matrix.saveOk'))
      } catch (e) {
        setOverview(prev)
        void message.error(teamErrorMessage(e, t('perm.matrix.saveFailed')))
      } finally {
        setSavingCell(null)
      }
    },
    [canEditMatrix, workspaceId, t],
  )

  const rows = overview?.capabilities ?? []
  const counts = overview?.counts
  const cards: Array<{ key: 'owner' | 'admin' | 'member' | 'readonly'; icon: React.ReactNode; tone: string }> = [
    { key: 'owner', icon: <CrownOutlined />, tone: 'owner' },
    { key: 'admin', icon: <SafetyCertificateOutlined />, tone: 'admin' },
    { key: 'member', icon: <UserOutlined />, tone: 'member' },
    { key: 'readonly', icon: <LockOutlined />, tone: 'readonly' },
  ]

  const columns: ColumnsType<TeamMember> = [
    {
      title: t('perm.members.col.member'),
      key: 'member',
      render: (_: unknown, r) => (
        <div className="tm-member-cell">
          <TeamAvatar name={r.display_name} size={34} round />
          <div className="tm-member-name">
            {r.display_name}
            {r.is_self && <span className="tm-self">{t('members.you')}</span>}
          </div>
        </div>
      ),
    },
    { title: t('perm.members.col.account'), key: 'account', render: (_: unknown, r) => <span className="tm-hint">{r.email}</span> },
    {
      title: t('perm.members.col.role'),
      key: 'role',
      width: 130,
      render: (_: unknown, r) => <span className={`tm-tag tm-tag-${r.role_key}`}>{t(`role.${r.role_key}`)}</span>,
    },
    {
      title: t('perm.members.col.scope'),
      key: 'scope',
      render: (_: unknown, r) => (
        <span className="tm-hint" style={{ fontSize: 13 }}>
          {t(`perm.members.scope.${r.role_key}`)}
          {overview ? ` · ${t('perm.members.scopeCount', { n: grantedCount(rows, r.role_key) })}` : ''}
        </span>
      ),
    },
    {
      title: t('perm.members.col.status'),
      key: 'status',
      width: 100,
      render: (_: unknown, r) => <span className={`tm-tag ${r.status === 'active' ? 'tm-tag-on' : 'tm-tag-off'}`}>{t(`status.${r.status}`)}</span>,
    },
    {
      title: t('perm.members.col.actions'),
      key: 'actions',
      width: 120,
      render: (_: unknown, r) => (
        <Button type="link" size="small" onClick={() => setSelected(r)} data-testid={`team-perm-open-${r.user_id}`}>
          {canSetRole(overview?.you.role_key, r) ? t('perm.members.adjust') : t('perm.members.view')}
        </Button>
      ),
    },
  ]

  return (
    <div className="tm-perm" data-testid="team-permissions">
      {overviewError && (
        <Alert
          type="error"
          showIcon
          title={overviewError}
          style={{ marginBottom: 12 }}
          action={
            <Button size="small" onClick={() => void loadOverview()}>
              {t('common.retry')}
            </Button>
          }
        />
      )}

      <div className="tm-row tm-row-count" data-testid="team-perm-role-cards">
        {cards.map((c) => (
          <div className={`tm-card tm-perm-card is-${c.tone}`} key={c.key} data-testid={`team-perm-card-${c.key}`}>
            <span className="tm-perm-card-icon">{c.icon}</span>
            <div className="tm-perm-card-text">
              <span className="tm-card-label">{t(`perm.cards.${c.key}`)}</span>
              <strong className="tm-metric-value">{counts ? counts[c.key] : '—'}</strong>
              <span className="tm-hint">{t(`perm.cards.${c.key}Desc`)}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="tm-perm-bar">
        <div className="tm-seg" role="tablist" aria-label={t('perm.title')}>
          {(['members', 'matrix', 'function'] as const).map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={view === v}
              className={`tm-seg-item${view === v ? ' is-active' : ''}`}
              onClick={() => setView(v)}
              data-testid={`team-perm-view-${v}`}
            >
              {t(`perm.tabs.${v}`)}
            </button>
          ))}
        </div>
        <Button icon={<PlusOutlined />} type="primary" onClick={() => navigate(`/workspace/${workspaceId}/team/members?create=1`)} data-testid="team-perm-create-account">
          {t('perm.createAccount')}
        </Button>
      </div>

      {view === 'function' ? (
        <section className="tm-card" data-testid="team-perm-function-auth" style={{ padding: 16 }}>
          <MemberAuthMatrixPanel
            workspaceId={workspaceId}
            members={authRoster.map((m) => {
              const canSelect = canSelectMemberAuthTarget(overview?.you.role_key, m)
              const canEditGrants = canManageMemberAuth(overview?.you.role_key, m)
              const isOwnerTarget = m.role === 'owner'
              return {
                user_id: m.user_id,
                display_name: m.is_self ? `${m.display_name}（我）` : m.display_name,
                email: m.email,
                role: m.role,
                role_label: t(`role.${m.role_key}`),
                disabled: !canSelect,
                memberGrantReadOnly: !canEditGrants,
                disabledReason: !canSelect
                  ? isOwnerTarget
                    ? t('perm.functionAuth.ownerManagedByPlatform')
                    : t('perm.functionAuth.cannotManage')
                  : isOwnerTarget
                    ? t('perm.functionAuth.ownerFullWorkspace')
                    : undefined,
              }
            })}
            actor={MEMBER_AUTH_ACTOR}
            canManageTeamSwitch={overview?.you.role_key === 'owner'}
          />
        </section>
      ) : view === 'members' ? (
        <section className="tm-card" data-testid="team-perm-members">
          <div className="tm-toolbar">
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder={t('perm.members.search')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ maxWidth: 280 }}
              data-testid="team-perm-search"
            />
            <Select<TeamRoleKey>
              allowClear
              placeholder={t('members.filterRole')}
              value={role}
              onChange={(v) => {
                setRole(v)
                setPage(1)
              }}
              style={{ width: 150 }}
              options={[
                { value: 'owner', label: t('role.owner') },
                { value: 'admin', label: t('role.admin') },
                { value: 'member', label: t('role.member') },
              ]}
            />
            <Select<MemberStatus>
              allowClear
              placeholder={t('members.filterStatus')}
              value={status}
              onChange={(v) => {
                setStatus(v)
                setPage(1)
              }}
              style={{ width: 140 }}
              options={[
                { value: 'active', label: t('status.active') },
                { value: 'disabled', label: t('status.disabled') },
              ]}
            />
          </div>
          {error && (
            <Alert
              type="error"
              showIcon
              title={error}
              style={{ marginBottom: 12 }}
              action={
                <Button size="small" onClick={() => void loadMembers()}>
                  {t('common.retry')}
                </Button>
              }
            />
          )}
          <Table<TeamMember>
            rowKey="user_id"
            size="middle"
            loading={loading}
            columns={columns}
            dataSource={items}
            locale={{ emptyText: t('perm.members.empty') }}
            pagination={{ current: page, pageSize: PAGE_SIZE, total, showSizeChanger: false, hideOnSinglePage: true, onChange: setPage }}
            onRow={(r) => ({ onClick: () => setSelected(r), style: { cursor: 'pointer' } })}
            scroll={{ x: 820 }}
          />
          <div className="tm-hint" style={{ marginTop: 8 }}>
            {t('perm.auditNote')}
          </div>
        </section>
      ) : (
        <section className="tm-card" data-testid="team-perm-matrix">
          <div className="tm-card-head">
            <span className="tm-card-title">{t('perm.matrix.title')}</span>
          </div>
          <Alert
            type="info"
            showIcon
            title={canEditMatrix ? t('perm.matrix.hintEditable') : t('perm.matrix.hint')}
            style={{ marginBottom: 14 }}
          />
          {!overview ? (
            <Skeleton active paragraph={{ rows: 8 }} />
          ) : (
            <div className="tm-matrix-wrap">
              <table className="tm-matrix">
                <thead>
                  <tr>
                    <th>{t('perm.matrix.col.capability')}</th>
                    {(['owner', 'admin', 'member'] as const).map((r) => (
                      <th key={r} className="is-center">
                        {t(`role.${r}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupCapabilities(rows).map((g) => (
                    <FragmentGroup
                      key={g.group}
                      group={g.group}
                      rows={g.rows}
                      canEditMatrix={canEditMatrix}
                      savingKey={savingCell}
                      onToggle={toggleCapability}
                      onOpenEntry={() => setView('function')}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="tm-matrix-legend">
            <span>
              <CheckCircleFilled className="is-full" /> {t('perm.matrix.level.full')}
            </span>
            <span>
              <MinusCircleFilled className="is-limited" /> {t('perm.matrix.level.limited')}
            </span>
            <span>
              <CloseCircleOutlined className="is-none" /> {t('perm.matrix.level.none')}
            </span>
            <span className="tm-perm-soon">{t('perm.matrix.status.entry')}</span>
            <span className="tm-perm-soon">{t('perm.matrix.status.coming_soon')}</span>
          </div>
        </section>
      )}

      <PermissionDrawer
        workspaceId={workspaceId}
        member={selected}
        overview={overview}
        onClose={() => setSelected(null)}
        onSaved={() => {
          setSelected(null)
          refreshAll()
        }}
        onStale={() => {
          setSelected(null)
          refreshAll()
        }}
      />
    </div>
  )
}

function FragmentGroup({
  group,
  rows,
  canEditMatrix,
  savingKey,
  onToggle,
  onOpenEntry,
}: {
  group: string
  rows: PermissionCapability[]
  canEditMatrix?: boolean
  savingKey?: string | null
  onToggle?: (row: PermissionCapability, role: TeamRoleKey, next: PermissionLevel) => void
  onOpenEntry?: () => void
}) {
  const { t } = useTranslation('team')
  return (
    <>
      <tr className="tm-matrix-group">
        <td colSpan={4}>{t(`perm.matrix.group.${group}`)}</td>
      </tr>
      {rows.map((row) => (
        <tr key={row.key} data-testid={`team-perm-row-${row.key}`}>
          <td>
            {t(`perm.cap.${row.key}`)}
            {row.status === 'entry' && (
              <div className="tm-hint" style={{ fontSize: 12, marginTop: 2 }}>
                {t('perm.matrix.entryHint')}
              </div>
            )}
            {row.status === 'coming_soon' && row.note === 'asset_visibility_only' && (
              <div className="tm-hint" style={{ fontSize: 12, marginTop: 2 }}>
                {t('perm.cap.note.asset_visibility_only')}
              </div>
            )}
          </td>
          {row.status === 'entry' ? (
            <td colSpan={3} className="is-center">
              <LevelCell row={row} role="owner" onOpenEntry={onOpenEntry} />
            </td>
          ) : (
            (['owner', 'admin', 'member'] as const).map((r) => (
              <td key={r} className="is-center">
                <LevelCell
                  row={row}
                  role={r}
                  noteText={row.note ? t(`perm.cap.note.${row.note}`, { defaultValue: '' }) : undefined}
                  canEditMatrix={canEditMatrix}
                  savingKey={savingKey}
                  onToggle={onToggle}
                  onOpenEntry={onOpenEntry}
                />
              </td>
            ))
          )}
        </tr>
      ))}
    </>
  )
}

function PermissionDrawer({
  workspaceId,
  member,
  overview,
  onClose,
  onSaved,
  onStale,
}: {
  workspaceId: string
  member: TeamMember | null
  overview: PermissionsOverview | null
  onClose: () => void
  onSaved: () => void
  onStale: () => void
}) {
  const { t } = useTranslation('team')
  const [draft, setDraft] = useState<'admin' | 'member'>('member')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (member) setDraft(member.role_key === 'admin' ? 'admin' : 'member')
  }, [member])

  const actorRole = overview?.you.role_key
  const rows = overview?.capabilities ?? []
  const editable = !!member && member.status === 'active' && canSetRole(actorRole, member)
  const changed = !!member && editable && draft !== (member.role_key === 'admin' ? 'admin' : 'member')

  let note: string | null = null
  if (member) {
    if (member.role_key === 'owner') note = t('perm.drawer.ownerNote')
    else if (member.is_self) note = t('perm.drawer.selfNote')
    else if (actorRole !== 'owner') note = t('perm.drawer.onlyOwner')
    else if (member.status !== 'active') note = t('perm.drawer.disabledNote')
  }

  const save = async () => {
    if (!member || !changed) return
    setSaving(true)
    try {
      await setMemberRole(workspaceId, member.user_id, { role: draft, expected_role: member.role_key })
      void message.success(t(draft === 'admin' ? 'perm.drawer.appointed' : 'perm.drawer.revoked', { name: member.display_name }))
      onSaved()
    } catch (e) {
      if (teamErrorCode(e) === 'role_changed_concurrently') {
        void message.warning(t('perm.drawer.stale'))
        onStale()
      } else {
        void message.error(teamErrorMessage(e, t('perm.drawer.failed')))
      }
    } finally {
      setSaving(false)
    }
  }

  const futureRows = rows.filter((r) => r.status === 'coming_soon' || r.status === 'entry')
  const active = rows.filter((r) => r.status === 'active')

  return (
    <Drawer
      open={!!member}
      onClose={onClose}
      size={440}
      title={t('perm.drawer.title')}
      destroyOnHidden
      data-testid="team-perm-drawer"
      footer={
        <div className="tm-drawer-footer">
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          {editable && (
            <Button type="primary" disabled={!changed} loading={saving} onClick={() => void save()} data-testid="team-perm-save">
              {t('perm.drawer.save')}
            </Button>
          )}
        </div>
      }
    >
      {member && (
        <div className="tm-drawer">
          <div className="tm-drawer-head">
            <TeamAvatar name={member.display_name} size={52} round />
            <div>
              <div className="tm-drawer-name">{member.display_name}</div>
              <div className="tm-hint">{member.email}</div>
              <span className={`tm-tag ${member.status === 'active' ? 'tm-tag-on' : 'tm-tag-off'}`} style={{ marginTop: 6 }}>
                {t(`status.${member.status}`)}
              </span>
            </div>
          </div>

          <div className="tm-section-title">{t('perm.drawer.roleTitle')}</div>
          <Select<'admin' | 'member'>
            style={{ width: '100%' }}
            value={member.role_key === 'owner' ? undefined : draft}
            placeholder={t('role.owner')}
            disabled={!editable}
            onChange={setDraft}
            options={[
              { value: 'admin', label: t('role.admin') },
              { value: 'member', label: t('role.member') },
            ]}
            data-testid="team-perm-role-select"
          />
          <p className="tm-hint" style={{ margin: '8px 0 0' }}>
            {t(`perm.drawer.roleHelp.${member.role_key}`)}
          </p>
          {note && <Alert type="info" showIcon title={note} style={{ marginTop: 10 }} />}

          <div className="tm-section-title">{t('perm.drawer.capsTitle')}</div>
          <p className="tm-hint" style={{ margin: '0 0 8px' }}>
            {t('perm.drawer.capsHint')}
          </p>
          <ul className="tm-cap-list" data-testid="team-perm-caps">
            {active.map((row) => (
              <li key={row.key}>
                <span>{t(`perm.cap.${row.key}`)}</span>
                <LevelCell row={row} role={editable ? draft : member.role_key} noteText={row.note ? t(`perm.cap.note.${row.note}`) : undefined} />
              </li>
            ))}
          </ul>

          <div className="tm-section-title">{t('perm.drawer.advancedTitle')}</div>
          <Alert type="info" showIcon title={t('perm.drawer.advancedHint')} style={{ marginBottom: 8 }} />
          <ul className="tm-cap-list is-soon" data-testid="team-perm-future">
            {futureRows.map((row) => (
              <li key={row.key}>
                <span>{t(`perm.cap.${row.key}`)}</span>
                <span className="tm-perm-soon">
                  {row.status === 'entry' ? t('perm.matrix.status.entry') : t('perm.matrix.status.coming_soon')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Drawer>
  )
}
